// src/plugins/axios.js - v2
import axios from 'axios'
import router from '@/plugins/router'
import { getLocalData, removeLocalData } from '@/utils/localdata'

// Función para detectar si estamos en plataforma nativa (evaluada en cada petición)
const checkIsNative = () => {
  return window.Capacitor?.isNativePlatform?.() ?? false
}

// Timeout por defecto para peticiones nativas (10 segundos)
const NATIVE_REQUEST_TIMEOUT = 10000

// Crear instancia base de Axios (para web y fallback)
const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

// Función auxiliar para obtener el token
const getAuthToken = async () => {
  const token = await getLocalData('authToken')
  if (token && token !== 'undefined' && token !== 'null' && token.trim() !== '') {
    try {
      return JSON.parse(token)
    } catch (error) {
      return null
    }
  }
  return null
}

// Función auxiliar para manejar errores 401
const handle401Error = async (url) => {
  const currentPath = router.currentRoute?.value?.path || ''
  const isExcludedFlow = url.includes('/register') || url.includes('/recoverpass') ||
                         currentPath === '/auth/register' || currentPath.startsWith('/app/help')

  if (!isExcludedFlow) {
    await removeLocalData('userData')
    await removeLocalData('authToken')

    try {
      router.push('/auth/login')
    } catch {
      window.location = '/auth/login'
    }
  }
}

// Helper para crear un timeout promise
const createTimeout = (ms, url) => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Request timeout after ${ms}ms: ${url}`))
    }, ms)
  })
}

// Cliente HTTP nativo usando fetch (funciona en iOS sin CORS)
const nativeHttpRequest = async (config) => {
  let url = config.url

  try {
    let baseURL = process.env.VUE_APP_API_URL

    // Quitar barra final de baseURL si existe
    if (baseURL && baseURL.endsWith('/')) {
      baseURL = baseURL.slice(0, -1)
    }

    // Construir URL completa si es relativa
    if (url && !url.startsWith('http')) {
      if (!url.startsWith('/')) {
        url = '/' + url
      }
      url = baseURL + url
    }

    // Añadir query params a la URL
    if (config.params) {
      const queryString = new URLSearchParams(config.params).toString()
      url += (url.includes('?') ? '&' : '?') + queryString
    }

    // Preparar headers
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...config.headers
    }

    // Añadir token de autorización
    const token = await getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const method = (config.method || 'GET').toUpperCase()
    const timeout = config.timeout || NATIVE_REQUEST_TIMEOUT

    const startTime = Date.now()

    // Configurar opciones de fetch
    const fetchOptions = {
      method: method,
      headers: headers
    }

    // Añadir body para POST/PUT/PATCH
    if (config.data && ['POST', 'PUT', 'PATCH'].includes(method)) {
      fetchOptions.body = JSON.stringify(config.data)
    }

    // Ejecutar fetch con timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)
    fetchOptions.signal = controller.signal

    const response = await fetch(url, fetchOptions)
    clearTimeout(timeoutId)

    // Parsear respuesta
    let data
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      data = await response.json()
    } else {
      data = await response.text()
    }

    // Manejar errores HTTP
    if (!response.ok) {
      if (response.status === 401) {
        await handle401Error(config.url || '')
      }
      const error = new Error(`HTTP ${response.status}`)
      error.response = { data, status: response.status, headers: {} }
      error.config = config
      throw error
    }

    return {
      data: data,
      status: response.status,
      headers: {},
      config: config
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      error.message = `Request timeout after ${config.timeout || NATIVE_REQUEST_TIMEOUT}ms: ${url}`
    }

    const axiosError = new Error(error.message || 'Request failed')
    axiosError.response = error.response || { data: null, status: 0, headers: {} }
    axiosError.config = config
    throw axiosError
  }
}

// Interceptor de REQUEST para axios (web) → añade token automáticamente
axiosInstance.interceptors.request.use(async (config) => {
  const token = await getAuthToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Interceptor de RESPONSE para axios (web) → maneja errores 401
axiosInstance.interceptors.response.use(
  response => response,
  async (error) => {
    if (error?.response?.status === 401) {
      const url = error.config?.url || ''
      await handle401Error(url)
    }
    return Promise.reject(error)
  }
)

// Cliente API unificado
const $api = {
  async get(url, config = {}) {
    if (checkIsNative()) {
      return nativeHttpRequest({ ...config, method: 'GET', url })
    }
    return axiosInstance.get(url, config)
  },

  async post(url, data, config = {}) {
    if (checkIsNative()) {
      return nativeHttpRequest({ ...config, method: 'POST', url, data })
    }
    return axiosInstance.post(url, data, config)
  },

  async put(url, data, config = {}) {
    if (checkIsNative()) {
      return nativeHttpRequest({ ...config, method: 'PUT', url, data })
    }
    return axiosInstance.put(url, data, config)
  },

  async patch(url, data, config = {}) {
    if (checkIsNative()) {
      return nativeHttpRequest({ ...config, method: 'PATCH', url, data })
    }
    return axiosInstance.patch(url, data, config)
  },

  async delete(url, config = {}) {
    if (checkIsNative()) {
      return nativeHttpRequest({ ...config, method: 'DELETE', url })
    }
    return axiosInstance.delete(url, config)
  },

  async request(config) {
    if (checkIsNative()) {
      return nativeHttpRequest(config)
    }
    return axiosInstance.request(config)
  }
}

export default $api
